import React from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import { Company } from './lib/types';
import { ActionRow } from './lib/selectors';
import { QUEUE_SIZE, decideQueue, decideStats, waitingCount } from './lib/decideRank';
import { DecisionCard } from './DecisionCard';
import { ACCENT, FONT, RADIUS, SHADOW } from './lib/brand';

/**
 * "Decide today": the few pending items that matter most, ranked, each answerable
 * in place. The point is that Grant can clear the day's decisions in ten minutes
 * without reading 151 rows. The full list stays in the Action Center below.
 */
export function DecisionQueue({
  rows, company, companyName, renderDecision, onOpenAction, onOpenBot, onWhy, onSeeAll,
}: {
  rows: ActionRow[]; company: Company; companyName: string;
  renderDecision: (row: ActionRow) => React.ReactNode;
  onOpenAction: (id: string) => void; onOpenBot: (slug: string) => void; onWhy?: (row: ActionRow) => void;
  onSeeAll: () => void;
}) {
  const [limit, setLimit] = React.useState(QUEUE_SIZE);
  const [keep, setKeep] = React.useState<Set<string>>(new Set());
  React.useEffect(() => { setLimit(QUEUE_SIZE); setKeep(new Set()); }, [company]);
  const touch = React.useCallback((id: string) => setKeep((k) => (k.has(id) ? k : new Set(k).add(id))), []);

  const queue = React.useMemo(() => decideQueue(rows, keep), [rows, keep]);
  const waiting = waitingCount(rows);
  const stats = React.useMemo(() => decideStats(rows), [rows]);
  const shown = queue.slice(0, limit);
  const accent = ACCENT[company].acc;

  return (
    <Paper variant="outlined" component="section" id="bo-decide" aria-labelledby="bo-decide-title" data-testid="decide-queue" sx={{ borderRadius: RADIUS.xl, boxShadow: SHADOW.card, overflow: 'hidden', mb: 2 }}>
      <Box sx={{ p: 2, pb: 1.4, display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography id="bo-decide-title" component="h2" sx={{ fontFamily: FONT.display, fontSize: 19, fontWeight: 700, color: 'primary.main' }}>Decide today</Typography>
        <Typography variant="body2" data-testid="decide-count" sx={{ fontWeight: 700 }}>
          {waiting === 0 ? `Nothing waiting · ${companyName}` : `${Math.min(shown.length, waiting)} of ${waiting} waiting · ${companyName}`}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary" data-testid="decide-stats">
          Decided today {stats.decidedToday} · In progress {stats.inProgress} · Done this week {stats.doneThisWeek}
        </Typography>
      </Box>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ px: 2, mt: -0.8, mb: 1 }}>
        Ranked: exec alternatives first, then P0 → P2, score, oldest. Yes queues it for the Implementation Bot; nothing executes from this page.
      </Typography>

      {shown.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 2 }}>Every item is decided. New recommendations will appear here.</Typography>
      ) : (
        <Box>
          {shown.map((r, i) => (
            <DecisionCard
              key={r.action.id}
              row={r}
              rank={i + 1}
              accent={accent}
              renderDecision={renderDecision}
              onOpenAction={onOpenAction}
              onOpenBot={onOpenBot}
              onWhy={onWhy}
              onTouch={touch}
            />
          ))}
        </Box>
      )}

      {(queue.length > shown.length || waiting > 0) && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          {queue.length > shown.length && (
            <Button size="small" variant="outlined" onClick={() => setLimit((n) => n + QUEUE_SIZE)}>
              Show {Math.min(QUEUE_SIZE, queue.length - shown.length)} more
            </Button>
          )}
          {waiting > 0 && <Button size="small" onClick={onSeeAll}>See all {waiting} in the Action Center</Button>}
        </Box>
      )}
    </Paper>
  );
}
